import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['modal', 'nameInput', 'form'];
  static values = { translations: Object };

  connect() {
    // Initialize form state management
    this.updateStateFromForm();
    this.setupFormListeners();

    // Bind methods to preserve 'this' context
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleFormSubmitStart = this.handleFormSubmitStart.bind(this);
    this.showFlashMessage = this.showFlashMessage.bind(this);
    this.createFallbackNotification = this.createFallbackNotification.bind(this);
  }

  showModal() {
    this.updateModalContent();

    const dropdown = document.querySelector('#saved-searches-dropdown');
    const currentSearchName = dropdown?.dataset?.currentSearchName;

    if (currentSearchName) {
      this.nameInputTarget.value = currentSearchName;
    }
    this.modalTarget.classList.remove('tw-hidden');
    this.nameInputTarget.focus();

    const form = this.modalTarget.querySelector('form');
    if (form) {
      form.addEventListener('turbo:submit-end', this.handleFormSubmit);
      form.addEventListener('submit', this.handleFormSubmitStart);
    }
  }

  hideModal() {
    this.modalTarget.classList.add('tw-hidden');
    this.nameInputTarget.value = '';

    const form = this.modalTarget.querySelector('form');
    if (form) {
      form.removeEventListener('turbo:submit-end', this.handleFormSubmit);
      form.removeEventListener('submit', this.handleFormSubmitStart);
    }
  }

  modalKeydown(event) {
    if (event.key === 'Escape') {
      this.hideModal();
    }
  }

  handleFormSubmitStart = (event) => {
    event.preventDefault();

    const form = event.target;
    const formData = new FormData(form);

    const submitter = event.submitter;
    const saveAndRun = submitter && submitter.name === 'save_and_run';

    if (saveAndRun) {
      formData.append('save_and_run', 'true');
    }

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          return response.text().then((_text) => {
            throw new Error('Server returned non-JSON response');
          });
        }

        return response.json();
      })
      .then((data) => {
        if (data.success) {
          this.showFlashMessage(data.message, 'notice');
          this.hideModal();
          this.refreshSavedSearches();
          if (saveAndRun && data.redirect_url) {
            window.location.href = data.redirect_url;
          }
        } else {
          this.showFlashMessage(data.message, 'alert');
        }
      })
      .catch((error) => {
        this.showFlashMessage(`Error: ${error.message}`, 'alert');
      });
  };

  handleFormSubmit = () => {
    // Placeholder for turbo:submit-end event
  };

  saveSearch = (event) => {
    const form = event.target.closest('form');
    if (!form) {
      return;
    }

    this.handleFormSubmitStart({ target: form, preventDefault: () => {} });
  };

  showFlashMessage = (message, type) => {
    const notificationContainer = document.getElementById('notification');
    if (!notificationContainer) {
      this.createFallbackNotification(message, type);
      return;
    }

    const existingFlash = document.getElementById('note');
    if (existingFlash) {
      existingFlash.remove();
    }

    const flashElement = document.createElement('div');
    flashElement.id = 'note';
    flashElement.className = `${type === 'notice' ? 'tw-bg-green-500' : 'tw-bg-red-500'} tw-text-white tw-p-4 tw-rounded tw-mb-4 tw-flex tw-items-center tw-justify-between`;

    flashElement.innerHTML = `
      <div class="tw-flex tw-items-center tw-gap-2">
        <i class="fa-solid ${type === 'notice' ? 'fa-check' : 'fa-exclamation-triangle'}"></i>
        <span>${message}</span>
      </div>
      <button onclick="this.parentElement.remove()" class="tw-text-white hover:tw-text-gray-200">
        <i class="fa-solid fa-times"></i>
      </button>
    `;

    notificationContainer.appendChild(flashElement);

    setTimeout(() => {
      if (flashElement && flashElement.parentNode) {
        flashElement.remove();
      }
    }, 5000);
  };

  createFallbackNotification = (message, type) => {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      padding: 16px;
      border-radius: 8px;
      color: white;
      font-weight: 500;
      max-width: 400px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      background-color: ${type === 'notice' ? '#10b981' : '#ef4444'};
    `;
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <i class="fa-solid ${type === 'notice' ? 'fa-check' : 'fa-exclamation-triangle'}"></i>
        <span>${message}</span>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 5000);
  };

  refreshSavedSearches() {
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set('refresh_searches', 'true');

    fetch(currentUrl.toString(), {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newSearchesSelect = doc.querySelector('#saved-searches-dropdown');
        const currentSearchesSelect = document.querySelector('#saved-searches-dropdown');

        if (newSearchesSelect && currentSearchesSelect) {
          currentSearchesSelect.outerHTML = newSearchesSelect.outerHTML;
        } else if (newSearchesSelect && !currentSearchesSelect) {
          const savedSearchContainer = document.querySelector('[data-controller*="saved-search"]');
          if (savedSearchContainer) {
            const searchForm = savedSearchContainer.querySelector('form[id*="userFilters"], form[name*="userFilters"]');
            if (searchForm) {
              searchForm.insertAdjacentHTML('beforebegin', newSearchesSelect.outerHTML);
            } else {
              const headerSection = savedSearchContainer.querySelector('.tw-mb-6.tw-text-center');
              if (headerSection) {
                headerSection.insertAdjacentHTML('afterend', newSearchesSelect.outerHTML);
              }
            }
          }
        } else if (!newSearchesSelect && currentSearchesSelect) {
          currentSearchesSelect.remove();
        }
      })
      .catch((error) => {
        console.error(error.message);
      });
  }

  saveWithCurrentState = (event) => {
    const form = event.target;
    const state = this.getCurrentFormState();

    form.querySelector('[data-saved-search-target="sectorField"]').value = state.sector_id;
    form.querySelector('[data-saved-search-target="tradeField"]').value = state.trade_id;
    form.querySelector('[data-saved-search-target="provinceField"]').value = state.province_id;
    form.querySelector('[data-saved-search-target="cityField"]').value = state.city_id;
    form.querySelector('[data-saved-search-target="levelField"]').value = state.level_id;
    form.querySelector('[data-saved-search-target="favouritesField"]').value = state.filter_favourites;
    form.querySelector('[data-saved-search-target="searchField"]').value = state.search;
    form.querySelector('[data-saved-search-target="fulltimeField"]').value = state.fulltime;
  };

  updateStateFromForm() {
    const form = document.getElementById('userFilters');
    if (!form) {
      return;
    }

    const formData = new FormData(form);
    const fulltimeValue = formData.get('fulltime');

    this.currentState = {
      sector_id: this.getArrayFromFormData(formData, 'sector_id').join(','),
      trade_id: this.getArrayFromFormData(formData, 'trade_id').join(','),
      province_id: this.getArrayFromFormData(formData, 'province_id').join(','),
      city_id: this.getArrayFromFormData(formData, 'city_id').join(','),
      level_id: this.getArrayFromFormData(formData, 'level_id').join(','),
      filter_favourites: formData.get('filter_favourites') === 'true',
      search: formData.get('search'),
      fulltime: fulltimeValue === null || fulltimeValue === '' ? null : fulltimeValue === 'true',
    };
  }

  setupFormListeners() {
    const form = document.getElementById('userFilters');
    if (form) {
      form.addEventListener('change', () => this.updateStateFromForm());
    }
  }

  getArrayFromFormData(formData, fieldName) {
    return formData.getAll(fieldName).filter((v) => v);
  }

  updateModalContent() {
    const state = this.getCurrentFormState();
    const translations = this.translationsValue || {};

    const el = this.modalTarget.querySelector('#search-options-data');
    this.searchOptionsData = el ? JSON.parse(el.textContent) : {};

    const criteriaContainer = this.modalTarget.querySelector('#criteria-container');
    if (!criteriaContainer) {
      console.error("No element with id='criteria-container' within the modalTarget");
      return;
    }

    criteriaContainer.innerHTML = '';

    const criteria = [
      {
        criterion: state.sector_id,
        label: translations?.sectors || 'Sectors',
        value: this.getNamesFromIds('sectors', state.sector_id),
      },
      { criterion: state.trade_id, label: translations?.trades || 'Trades', value: this.getNamesFromIds('trades', state.trade_id) },
      {
        criterion: state.level_id,
        label: translations?.apprenticeship_level || 'Apprenticeship Level',
        value: this.getNamesFromIds('levels', state.level_id),
      },
      {
        criterion: state.province_id,
        label: translations?.provinces || 'Provinces',
        value: this.getNamesFromIds('provinces', state.province_id),
      },
      { criterion: state.city_id, label: translations?.cities || 'Cities', value: this.getNamesFromIds('cities', state.city_id) },
      {
        criterion: state.filter_favourites,
        label: translations?.filter_favourites || 'Filter Favourites',
        value: translations.yes || 'Yes',
      },
      { criterion: state.search, label: translations?.search || 'Search', value: state.search },
      {
        criterion: state.fulltime !== null,
        label: translations?.employment_type?.title || 'Job Type',
        value: state.fulltime ? translations?.employment_type?.fulltime : translations?.employment_type?.parttime,
      },
    ];

    criteria.forEach(({ criterion, label, value }) => {
      if (criterion) {
        this.addCriteriaItem(criteriaContainer, label, value);
      }
    });

    if (criteriaContainer.innerHTML === '') {
      criteriaContainer.innerHTML = `<div class="tw-text-gray-500 tw-text-sm">${translations?.empty || 'No search criteria selected'}</div>`;
    }
  }

  addCriteriaItem(container, label, value) {
    const div = document.createElement('div');
    div.innerHTML = `
      <span class="tw-font-medium tw-text-gray-600">${label}:</span>
      <span class="tw-text-gray-800">${value}</span>
    `;
    container.appendChild(div);
  }

  getNamesFromIds(keyPlural, ids) {
    if (!Object.hasOwn(this.searchOptionsData, keyPlural)) {
      return ids;
    }

    return ids
      .split(',')
      .filter((id) => id)
      .map((id) => this.searchOptionsData?.[keyPlural].find((item) => item.id.toString() === id.toString())?.name || `ID: ${id}`)
      .join(', ');
  }

  getCurrentFormState() {
    this.updateStateFromForm();

    return (
      this.currentState || {
        sector_id: '',
        trade_id: '',
        province_id: '',
        city_id: '',
        level_id: '',
        filter_favourites: false,
        search: '',
        fulltime: null,
      }
    );
  }
}
